import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import {
  SkeletonText,
  SkeletonPlaceholder,
  InlineNotification,
  Toggle,
  Tag,
} from '@carbon/react';
import {
  Information,
  UserMultiple,
  Building,
  User,
  Task,
  Money,
  Warning,
  WarningAlt,
  Location,
  Phone,
  Settings,
  Activity,
  ChartLine,
  Time,
  NotificationNew,
  View,
  Close,
} from '@carbon/icons-react';
import axiosInstance from '../utils/axiosInstance';
import ActionNeeded from '../components/ActionNeeded';
import NotificationContext from '../contexts/NotificationContext';
import {
  routeForNotification,
  formatNotificationTime,
  toastKindForType,
} from '../utils/notificationUtils';
import './HomePanel.css';

// ===========================================
// Konfiguracija tabova
// ===========================================
const TABS = [
  { id: 'info', label: 'Informacije', icon: Information },
  { id: 'stats', label: 'Statistika', icon: ChartLine },
  { id: 'actions', label: 'Akcije', icon: WarningAlt },
  { id: 'notifications', label: 'Obaveštenja', icon: NotificationNew },
];

// ===========================================
// Prevodi labela sa backenda
// ===========================================
const STATUS_LABELS = {
  waiting: 'Na čekanju',
  completed: 'Završeno',
  canceled: 'Otkazano',
  planned: 'Planirano',
  active: 'Aktivno',
};

const ACTIVITY_TYPE_LABELS = {
  Nutrition: 'Ishrana',
  Physical: 'Fizička',
  Medical: 'Medicinska',
  Nursing: 'Nega',
  Entertainment: 'Zabava',
  Rest: 'Odmor',
};

const SEVERITY_LABELS = {
  '1': 'Nizak',
  '2': 'Srednji',
  '3': 'Visok',
};

// ===========================================
// Pomoćne funkcije
// ===========================================
const numberFormat = new Intl.NumberFormat('sr-RS');

const fmt = (n) => (n === null || n === undefined ? '—' : numberFormat.format(n));

const fmtMoney = (n) =>
  n === null || n === undefined ? '—' : `${numberFormat.format(Math.round(n * 100) / 100)} RSD`;

// Backend procente vraća već pomnožene sa 100 (npr. 3.06 znači 3,06%), pa se
// ovde samo zaokružuju i ograničavaju na 0-100 radi sigurnosti.
const fmtPercent = (value) =>
  `${Math.min(100, Math.max(0, Math.round(value || 0)))}%`;

// Pretvara "2026-09-19" (ili ISO) u "19.09." za prikaz na X osi grafikona.
const fmtDayLabel = (value) => {
  if (!value) return '';
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[3]}.${match[2]}.`;
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${day}.${month}.`;
};

const mapLabels = (items = [], map = {}) =>
  (items || []).map((i) => ({ ...i, label: map[i.label] || i.label }));

// ===========================================
// Reusable komponente
// ===========================================
const StatTile = ({ label, value, sub, tone = 'default' }) => (
  <div className={`hp-stat hp-stat--${tone}`}>
    <span className="hp-stat__value">{value}</span>
    <span className="hp-stat__label">{label}</span>
    {sub && <span className="hp-stat__sub">{sub}</span>}
  </div>
);

const Card = ({ title, children, className = '' }) => (
  <div className={`hp-card ${className}`}>
    {title && <h4 className="hp-card__title">{title}</h4>}
    <div className="hp-card__body">{children}</div>
  </div>
);

const PanelSection = ({ title, icon: Icon, children }) => (
  <section className="hp-section">
    <header className="hp-section__head">
      {Icon && <Icon size={20} className="hp-section__icon" />}
      <h3 className="hp-section__title">{title}</h3>
    </header>
    <div className="hp-section__body">{children}</div>
  </section>
);

const GroupTitle = ({ title, icon: Icon, children }) => (
  <section className="hp-group">
    <header className="hp-group__head">
      {Icon && <Icon size={20} className="hp-group__icon" />}
      <h3 className="hp-group__title">{title}</h3>
    </header>
    <div className="hp-group__body">{children}</div>
  </section>
);

const InfoRow = ({ label, value, icon: Icon }) => (
  <div className="hp-row">
    <span className="hp-row__label">
      {Icon && <Icon size={16} />}
      {label}
    </span>
    <span className="hp-row__value">{value || '—'}</span>
  </div>
);

const BarList = ({ items = [], tone = 'accent' }) => {
  const list = items || [];
  const max = Math.max(1, ...list.map((i) => i.count || 0));
  if (list.length === 0) return <p className="hp-empty-text">Nema podataka.</p>;
  return (
    <div className="hp-bars">
      {list.map((item, idx) => {
        const pct = max > 0 ? Math.round(((item.count || 0) / max) * 100) : 0;
        return (
          <div className="hp-bar" key={`${item.label}-${idx}`}>
            <div className="hp-bar__head">
              <span className="hp-bar__label">{item.label}</span>
              <span className="hp-bar__value">{fmt(item.count)}</span>
            </div>
            <div className="hp-bar__track">
              <div
                className={`hp-bar__fill hp-bar__fill--${tone}`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const EmptyState = () => (
  <div className="hp-empty">
    <Information size={32} />
    <p>Nema dostupnih podataka.</p>
  </div>
);

const LoadingState = () => (
  <div className="hp-stack">
    <div className="hp-stats">
      {[0, 1, 2, 3].map((i) => (
        <div className="hp-stat hp-stat--skeleton" key={i}>
          <SkeletonText width="60%" />
          <SkeletonText width="80%" />
        </div>
      ))}
    </div>
    <div className="hp-grid hp-grid--2">
      {[0, 1].map((i) => (
        <div className="hp-card" key={i}>
          <SkeletonText width="40%" />
          <SkeletonPlaceholder style={{ height: '120px', marginTop: '0.75rem' }} />
        </div>
      ))}
    </div>
  </div>
);

// ===========================================
// Grafikoni (bez dodatnih biblioteka - čist SVG)
// ===========================================
const CHART_COLORS = [
  '#9fb746', '#0f62fe', '#f1c21b', '#da1e28', '#24a148',
  '#8a3ffc', '#007d79', '#ee5396', '#ff832b', '#a8a8a8',
];

const DonutChart = ({ items = [], size = 168, thickness = 26, centerLabel, centerValue }) => {
  const list = (items || []).filter((i) => (i.count || 0) > 0);
  const total = list.reduce((sum, i) => sum + (i.count || 0), 0);
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;

  if (total === 0) return <p className="hp-empty-text">Nema podataka.</p>;

  let offset = 0;
  return (
    <div className="hp-donut">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="hp-donut__svg">
        <g transform={`rotate(-90 ${center} ${center})`}>
          {list.map((item, idx) => {
            const fraction = (item.count || 0) / total;
            const dash = fraction * circumference;
            const seg = (
              <circle
                key={`${item.label}-${idx}`}
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                stroke={CHART_COLORS[idx % CHART_COLORS.length]}
                strokeWidth={thickness}
                strokeDasharray={`${dash} ${circumference - dash}`}
                strokeDashoffset={-offset}
              />
            );
            offset += dash;
            return seg;
          })}
        </g>
        <text x={center} y={center - 4} textAnchor="middle" className="hp-donut__value">
          {centerValue != null ? centerValue : fmt(total)}
        </text>
        {centerLabel && (
          <text x={center} y={center + 16} textAnchor="middle" className="hp-donut__label">
            {centerLabel}
          </text>
        )}
      </svg>
      <ul className="hp-legend">
        {list.map((item, idx) => (
          <li className="hp-legend__item" key={`${item.label}-${idx}`}>
            <span
              className="hp-legend__dot"
              style={{ backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }}
            />
            <span className="hp-legend__label">{item.label}</span>
            <span className="hp-legend__value">{fmt(item.count)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const ColumnChart = ({ items = [], height = 180, tone = 'accent' }) => {
  const list = items || [];
  if (list.length === 0) return <p className="hp-empty-text">Nema podataka.</p>;
  const max = Math.max(1, ...list.map((i) => i.count || 0));
  return (
    <div className="hp-columns" style={{ height }}>
      {list.map((item, idx) => {
        const pct = ((item.count || 0) / max) * 100;
        return (
          <div className="hp-columns__col" key={`${item.label}-${idx}`}>
            <span className="hp-columns__value">{fmt(item.count)}</span>
            <div className="hp-columns__track">
              <div
                className={`hp-columns__bar hp-columns__bar--${tone}`}
                style={{ height: `${Math.max(2, pct)}%` }}
              />
            </div>
            <span className="hp-columns__label">{item.label}</span>
          </div>
        );
      })}
    </div>
  );
};

const LineChart = ({ items = [], height = 200 }) => {
  const list = items || [];
  if (list.length === 0) return <p className="hp-empty-text">Nema podataka.</p>;

  const width = 720;
  const padX = 8;
  const padTop = 16;
  const padBottom = 24;
  const innerW = width - padX * 2;
  const innerH = height - padTop - padBottom;
  const max = Math.max(1, ...list.map((i) => i.count || 0));
  const stepX = list.length > 1 ? innerW / (list.length - 1) : 0;

  const points = list.map((item, idx) => {
    const x = padX + idx * stepX;
    const y = padTop + innerH - ((item.count || 0) / max) * innerH;
    return { x, y, item };
  });

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  const areaPath = `${linePath} L ${points[points.length - 1].x} ${padTop + innerH} L ${points[0].x} ${padTop + innerH} Z`;

  // Prikaži samo nekoliko labela na X osi da ne bude gužve
  const labelEvery = Math.max(1, Math.ceil(list.length / 6));

  return (
    <div className="hp-line">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        className="hp-line__svg"
        style={{ height }}
      >
        <defs>
          <linearGradient id="hp-line-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#9fb746" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#9fb746" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill="url(#hp-line-fill)" />
        <path d={linePath} fill="none" stroke="#9fb746" strokeWidth="2" vectorEffect="non-scaling-stroke" />
        {points.map((p, idx) => (
          <circle key={idx} cx={p.x} cy={p.y} r="2.5" fill="#9fb746" vectorEffect="non-scaling-stroke">
            <title>{`${fmtDayLabel(p.item.label)}: ${p.item.count}`}</title>
          </circle>
        ))}
      </svg>
      <div className="hp-line__axis">
        {list.map((item, idx) => {
          const show = idx % labelEvery === 0 || idx === list.length - 1;
          const leftPct = ((padX + idx * stepX) / width) * 100;
          return (
            <span
              key={`${item.label}-${idx}`}
              className="hp-line__tick"
              style={{ left: `${leftPct}%` }}
            >
              {show ? fmtDayLabel(item.label) : ''}
            </span>
          );
        })}
      </div>
    </div>
  );
};

// ===========================================
// Tabovi
// ===========================================
const InfoTab = ({ institution, settings, onSettingsChange }) => {
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const handleToggle = async (checked) => {
    setSaving(true);
    setSaveError(null);
    try {
      const res = await axiosInstance.patch(
        '/api/InstitutionSettings/employee-access-only-shift',
        { employeeAccessOnlyShift: checked }
      );
      const updated = res?.data?.data ?? null;
      if (updated) {
        onSettingsChange?.(updated);
      } else {
        onSettingsChange?.({ ...settings, employeeAccessOnlyShift: checked });
      }
    } catch (err) {
      console.error('Greška pri izmeni podešavanja pristupa:', err);
      setSaveError('Greška pri čuvanju podešavanja.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="hp-grid hp-grid--2">
      <PanelSection title="Informacije o domu" icon={Building}>
        <div className="hp-rows">
          <InfoRow label="Naziv" value={institution?.name} />
          <InfoRow label="Adresa" value={institution?.address} icon={Location} />
          <InfoRow
            label="Direktor"
            value={[institution?.directorFirstName, institution?.directorLastName]
              .filter(Boolean)
              .join(' ')}
            icon={User}
          />
          <InfoRow label="Kontakt" value={institution?.contact} icon={Phone} />
          <InfoRow label="Prefiks korisničkog imena" value={institution?.usernamePrefix} />
        </div>
      </PanelSection>

      <PanelSection title="Podešavanja doma" icon={Settings}>
        <div className="hp-rows">
          <div className="hp-setting">
            <Toggle
              id="hp-employee-access-only-shift"
              labelText="Pristup aplikaciji van smene za zaposlene"
              labelA="Off"
              labelB="On"
              toggled={!!settings?.employeeAccessOnlyShift}
              onToggle={handleToggle}
              disabled={saving}
            />
            <p className="hp-setting__hint">
              {settings?.employeeAccessOnlyShift
                ? 'Zaposleni mogu da pristupe aplikaciji samo u toku svoje smene.'
                : 'Zaposleni mogu da pristupe aplikaciji i van svoje smene.'}
            </p>
            <p className="hp-setting__hint">
              Da pojedinačnom zaposlenom dozvolite pristup podacima i van smene, otvorite{' '}
              <Link to="/employees" className="hp-setting__link">
                listu zaposlenih
              </Link>{' '}
              i to podesite na profilu tog zaposlenog.
            </p>
            {saveError && (
              <p className="hp-setting__error">{saveError}</p>
            )}
          </div>
        </div>
      </PanelSection>
    </div>
  );
};

const ResidentsTab = ({ data }) => {
  if (!data) return <EmptyState />;
  return (
    <div className="hp-stack">
      <div className="hp-stats">
        <StatTile label="Aktivni korisnici" value={fmt(data.totalActive)} tone="accent" />
        <StatTile label="Neaktivni korisnici" value={fmt(data.totalInactive)} />
        <StatTile
          label="Prosečna starost"
          value={data.averageAge != null ? data.averageAge : '—'}
          sub="godina"
        />
        <StatTile label="Novi (30 dana)" value={fmt(data.newLast30Days)} tone="info" />
      </div>

      {/* Pol + stepen nege */}
      <div className="hp-grid hp-grid--2">
        <Card title="Pol">
          <DonutChart items={data.gender} centerLabel="korisnika" />
        </Card>
        <Card title="Stepen nege">
          <DonutChart items={data.careLevel} centerLabel="korisnika" />
        </Card>
      </div>

      {/* Starosne grupe + pokretljivost */}
      <div className="hp-grid hp-grid--2">
        <Card title="Starosne grupe">
          <ColumnChart items={data.ageGroups} tone="info" />
        </Card>
        <Card title="Pokretljivost">
          <ColumnChart items={data.mobility} tone="warning" />
        </Card>
      </div>

      {/* Ostali pokazatelji */}
      <div className="hp-grid hp-grid--2">
        <Card title="Psihičko stanje"><BarList items={data.mentalState} /></Card>
        <Card title="Samoposluživanje"><BarList items={data.selfService} /></Card>
        <Card title="Higijena"><BarList items={data.hygiene} /></Card>
        <Card title="Komunikacija"><BarList items={data.communication} /></Card>
      </div>

      {/* Dokumentacija i ishrana */}
      <div className="hp-grid hp-grid--2">
        <Card title="Zdravstveni karton">
          <DonutChart
            items={[
              { label: 'Sa kartonom', count: data.withHealthRecordCount || 0 },
              { label: 'Bez kartona', count: data.withoutHealthRecordCount || 0 },
            ]}
            centerLabel="korisnika"
          />
        </Card>
        <Card title="Ishrana">
          <DonutChart
            items={[
              { label: 'Sa ishranom', count: data.withDietCount || 0 },
              { label: 'Bez ishrane', count: data.withoutDietCount || 0 },
            ]}
            centerLabel="korisnika"
          />
        </Card>
      </div>

      <div className="hp-grid hp-grid--3">
        <StatTile label="Koriste pomagala" value={fmt(data.usesSpecialAidsCount)} />
        <StatTile label="Sa zdravstvenim kartonom" value={fmt(data.withHealthRecordCount)} tone="success" />
        <StatTile label="Bez zdravstvenog kartona" value={fmt(data.withoutHealthRecordCount)} tone="critical" />
        <StatTile label="Sa ishranom" value={fmt(data.withDietCount)} tone="success" />
        <StatTile label="Bez ishrane" value={fmt(data.withoutDietCount)} tone="critical" />
      </div>
    </div>
  );
};

const RoomsTab = ({ data }) => {
  if (!data) return <EmptyState />;
  return (
    <div className="hp-stack">
      <div className="hp-stats">
        <StatTile label="Popunjenost" value={`${data.occupancyPercentage}%`} tone="accent" />
        <StatTile label="Ukupno soba" value={fmt(data.totalRooms)} />
        <StatTile label="Ukupno kreveta" value={fmt(data.totalBeds)} />
        <StatTile label="Zauzeti kreveti" value={fmt(data.takenBeds)} tone="info" />
        <StatTile label="Slobodni kreveti" value={fmt(data.freeBeds)} tone="success" />
      </div>

      <div className="hp-grid hp-grid--3">
        <StatTile label="Pune sobe" value={fmt(data.fullRooms)} tone="critical" />
        <StatTile label="Prazne sobe" value={fmt(data.emptyRooms)} tone="success" />
        <StatTile label="Delimično popunjene" value={fmt(data.partiallyOccupiedRooms)} tone="warning" />
      </div>

      <div className="hp-grid hp-grid--2">
        <Card title="Popunjenost po oblastima (%)">
          <ColumnChart
            items={(data.byArea || []).map((a) => ({
              label: a.areaName,
              count: a.occupancyPercentage,
            }))}
            tone="accent"
          />
        </Card>
        <Card title="Po stepenu nege">
          <DonutChart items={data.byCareLevel} centerLabel="korisnika" />
        </Card>
      </div>

      <Card title="Kreveti po oblastima">
        <div className="hp-areas">
          {(data.byArea || []).map((a) => (
            <div className="hp-area" key={a.areaId || a.areaName}>
              <div className="hp-area__head">
                <span className="hp-area__name">{a.areaName}</span>
                <span className="hp-area__pct">{a.occupancyPercentage}%</span>
              </div>
              <div className="hp-bar__track">
                <div
                  className="hp-bar__fill hp-bar__fill--accent"
                  style={{ width: `${a.occupancyPercentage}%` }}
                />
              </div>
              <div className="hp-area__meta">
                {fmt(a.takenBeds)} / {fmt(a.totalBeds)} kreveta
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

const EmployeesTab = ({ data }) => {
  if (!data) return <EmptyState />;
  return (
    <div className="hp-stack">
      <div className="hp-stats">
        <StatTile label="Aktivni zaposleni" value={fmt(data.totalActive)} tone="accent" />
        <StatTile label="Registrovani" value={fmt(data.registeredCount)} tone="success" />
        <StatTile label="Neregistrovani" value={fmt(data.unregisteredCount)} tone="warning" />
        <StatTile label="Obrisani" value={fmt(data.totalDeleted)} />
      </div>
      <div className="hp-grid hp-grid--2">
        <Card title="Po tipu">
          <DonutChart items={data.byType} centerLabel="zaposlenih" />
        </Card>
        <Card title="Zaposleni u poslednjih 12 meseci">
          <ColumnChart items={data.hiredLast12Months} tone="info" />
        </Card>
      </div>
    </div>
  );
};

const TasksTab = ({ tasks, activities, shifts }) => (
  <div className="hp-stack">
    {tasks && (
      <PanelSection title="Zadaci" icon={Task}>
        <div className="hp-stats">
          <StatTile label="Ukupno zadataka" value={fmt(tasks.total)} tone="accent" />
          <StatTile
            label="Stopa završetka"
            value={fmtPercent(tasks.completionRate)}
            tone="success"
          />
          <StatTile label="Nedodeljeni" value={fmt(tasks.unassignedCount)} tone="critical" />
          <StatTile label="Kreirani (30 dana)" value={fmt(tasks.createdLast30Days)} />
          <StatTile label="Završeni (30 dana)" value={fmt(tasks.completedLast30Days)} tone="success" />
        </div>
        <div className="hp-grid hp-grid--3">
          <Card title="Po statusu">
            <DonutChart items={mapLabels(tasks.byStatus, STATUS_LABELS)} centerLabel="zadataka" />
          </Card>
          <Card title="Po tipu aktivnosti">
            <DonutChart
              items={mapLabels(tasks.byActivityType, ACTIVITY_TYPE_LABELS)}
              centerLabel="zadataka"
            />
          </Card>
          <Card title="Po prioritetu">
            <DonutChart items={mapLabels(tasks.bySeverity, SEVERITY_LABELS)} centerLabel="zadataka" />
          </Card>
        </div>
      </PanelSection>
    )}

    {activities && (
      <PanelSection title="Aktivnosti" icon={Activity}>
        <div className="hp-stats">
          <StatTile label="Ukupno aktivnosti" value={fmt(activities.total)} tone="accent" />
        </div>
        <div className="hp-grid hp-grid--2">
          <Card title="Po statusu">
            <DonutChart items={mapLabels(activities.byStatus, STATUS_LABELS)} centerLabel="aktivnosti" />
          </Card>
          <Card title="Po tipu aktivnosti">
            <DonutChart
              items={mapLabels(activities.byActivityType, ACTIVITY_TYPE_LABELS)}
              centerLabel="aktivnosti"
            />
          </Card>
        </div>
        <Card title="Aktivnosti u poslednjih 30 dana">
          <LineChart items={activities.last30DaysPerDay} />
        </Card>
      </PanelSection>
    )}

    {shifts && (
      <PanelSection title="Smene" icon={Time}>
        <div className="hp-stats">
          <StatTile label="Ukupno smena" value={fmt(shifts.total)} tone="accent" />
          <StatTile label="Narednih 7 dana" value={fmt(shifts.upcomingNext7Days)} tone="info" />
          <StatTile label="Aktivno sada" value={fmt(shifts.activeNow)} tone="success" />
        </div>
        <div className="hp-grid hp-grid--2">
          <Card title="Po statusu">
            <DonutChart items={mapLabels(shifts.byStatus, STATUS_LABELS)} centerLabel="smena" />
          </Card>
          <Card title="Po oblastima">
            <ColumnChart items={shifts.byArea} tone="info" />
          </Card>
        </div>
      </PanelSection>
    )}
  </div>
);

const FinanceTab = ({ data }) => {
  if (!data) return <EmptyState />;
  const flow = data.monthlyFlowLast12Months || [];
  return (
    <div className="hp-stack">
      <div className="hp-stats">
        <StatTile label="Ukupno stanje" value={fmtMoney(data.totalBalance)} tone="accent" />
        <StatTile label="Prosečno stanje" value={fmtMoney(data.averageBalance)} />
        <StatTile label="Ukupan prihod" value={fmtMoney(data.totalIncome)} tone="success" />
        <StatTile label="Ukupan rashod" value={fmtMoney(data.totalExpense)} tone="critical" />
        <StatTile label="Prihod (30 dana)" value={fmtMoney(data.incomeLast30Days)} tone="success" />
        <StatTile label="Rashod (30 dana)" value={fmtMoney(data.expenseLast30Days)} tone="critical" />
      </div>
      <Card title="Mesečni tok (poslednjih 12 meseci)">
        {flow.length === 0 ? (
          <p className="hp-empty-text">Nema podataka.</p>
        ) : (
          <div className="hp-flow">
            {flow.map((m) => (
              <div className="hp-flow__row" key={m.month}>
                <span className="hp-flow__month">{m.month}</span>
                <span className="hp-flow__income">+{fmtMoney(m.income)}</span>
                <span className="hp-flow__expense">−{fmtMoney(m.expense)}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

const ActionsTab = ({ data }) => {
  if (!data) return <EmptyState />;
  return (
    <div className="hp-stack">
      <div className="hp-stats">
        <StatTile
          label="Nerešene akcije"
          value={fmt(data.totalUnresolved)}
          tone={data.totalUnresolved > 0 ? 'critical' : 'success'}
        />
      </div>
      <div className="hp-grid hp-grid--2">
        <Card title="Po prioritetu">
          <div className="hp-rows">
            {(data.bySeverity || []).map((s) => (
              <div className="hp-row" key={s.label}>
                <span className="hp-row__label">
                  {s.label === 'Najvažnije' ? <WarningAlt size={16} /> : <Warning size={16} />}
                  {s.label}
                </span>
                <span
                  className={`hp-badge ${
                    s.label === 'Najvažnije' ? 'hp-badge--critical' : 'hp-badge--warning'
                  }`}
                >
                  {fmt(s.count)}
                </span>
              </div>
            ))}
          </div>
        </Card>
        <Card title="Po tipu">
          <div className="hp-rows">
            {(data.byType || []).map((t) => (
              <div className="hp-row" key={t.label}>
                <span className="hp-row__label">{t.label}</span>
                <span className="hp-badge">{fmt(t.count)}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};

// ===========================================
// Obaveštenja (ista lista kao u zvoncetu u zaglavlju)
// ===========================================

// Nazivi tipova obaveštenja sa backenda (NotificationType) -> čitljiv naziv.
// Nepoznati tipovi se prikazuju bez oznake, pa mapa može mirno da raste.
const NOTIFICATION_TYPE_LABELS = {
  TaskAssigned: 'Zadatak dodeljen',
  TaskCompleted: 'Zadatak završen',
  TaskCanceled: 'Zadatak otkazan',
  TaskNoteAdded: 'Nova beleška o zadatku',
  ShiftAssigned: 'Smena dodeljena',
  ShiftChanged: 'Smena izmenjena',
  ShiftCanceled: 'Smena otkazana',
  ResidentDocumentAdded: 'Novi dokument stanara',
  ProblemReported: 'Prijavljen problem',
};

// Carbon `Tag` ne poznaje nazive toast tipova (success/error/...), pa ih prevodimo.
const TAG_TYPE_FOR_KIND = {
  success: 'green',
  error: 'red',
  warning: 'yellow',
  info: 'blue',
};

// Labela prečice ka povezanom entitetu (null kada prečice nema).
const linkLabelForNotification = (notification) => {
  if (!notification?.entityId) return null;
  switch (notification.entityType) {
    case 'Task':
    case 'TaskNote':
    case 'ProblemReport':
      return 'Otvori zadatak';
    case 'Shift':
      return 'Otvori smene';
    case 'Resident':
    case 'ResidentNote':
      return 'Otvori profil stanara';
    case 'ResidentDocument':
    case 7:
      return 'Otvori dokumenta';
    default:
      return null;
  }
};

const NotificationsTab = ({
  notifications = [],
  unreadCount = 0,
  loading = false,
  error = null,
  onMarkAsRead,
  onMarkAllAsRead,
  onDelete,
}) => {
  const showSkeleton = loading && notifications.length === 0;

  const markAsRead = (id) => {
    if (onMarkAsRead) onMarkAsRead(id);
  };

  return (
    <div className="hp-stack">
      {error && (
        <InlineNotification
          kind="error"
          title="Greška"
          subtitle={error}
          lowContrast
          hideCloseButton
          className="hp-notification"
        />
      )}

      <div className="hp-stats">
        <StatTile label="Ukupno obaveštenja" value={fmt(notifications.length)} tone="accent" />
        <StatTile
          label="Nepročitana"
          value={fmt(unreadCount)}
          tone={unreadCount > 0 ? 'info' : 'success'}
        />
      </div>
      <PanelSection title="Obaveštenja" icon={NotificationNew}>
        {unreadCount > 0 && (
          <div className="hp-notifs__toolbar">
            <button type="button" className="hp-notifs__mark-all" onClick={onMarkAllAsRead}>
              Označi sve kao pročitano
            </button>
          </div>
        )}

        {showSkeleton ? (
          <div className="hp-notifs">
            {[0, 1, 2].map((i) => (
              <div className="hp-notif hp-notif--skeleton" key={i}>
                <SkeletonText width="40%" />
                <SkeletonText width="90%" />
              </div>
            ))}
          </div>
        ) : notifications.length === 0 ? (
          <p className="hp-empty-text">Nema obaveštenja.</p>
        ) : (
          <div className="hp-notifs">
            {notifications.map((notification) => {
              const typeLabel = NOTIFICATION_TYPE_LABELS[notification.type];
              const linkLabel = linkLabelForNotification(notification);
              const route = routeForNotification(notification);

              return (
                <div
                  key={notification.id}
                  className={`hp-notif ${notification.isUnread ? 'hp-notif--unread' : ''}`}
                >
                  <div className="hp-notif__main">
                    <div className="hp-notif__head">
                      <h4 className="hp-notif__title">{notification.title}</h4>
                      {typeLabel && (
                        <Tag
                          type={TAG_TYPE_FOR_KIND[toastKindForType(notification.type)] || 'blue'}
                          size="sm"
                        >
                          {typeLabel}
                        </Tag>
                      )}
                    </div>
                    {notification.body && (
                      <p className="hp-notif__body">{notification.body}</p>
                    )}
                    <div className="hp-notif__meta">
                      <span className="hp-notif__time">
                        {formatNotificationTime(notification.createdAt)}
                      </span>
                      {linkLabel && route && (
                        <Link
                          to={route}
                          className="hp-notif__link"
                          onClick={() => markAsRead(notification.id)}
                        >
                          {linkLabel}
                        </Link>
                      )}
                    </div>
                  </div>

                  <div className="hp-notif__actions">
                    {notification.isUnread && (
                      <button
                        type="button"
                        className="hp-notif__action"
                        title="Označi kao pročitano"
                        aria-label="Označi kao pročitano"
                        onClick={() => markAsRead(notification.id)}
                      >
                        <View size={16} />
                      </button>
                    )}
                    <button
                      type="button"
                      className="hp-notif__action hp-notif__action--delete"
                      title="Obriši"
                      aria-label="Obriši obaveštenje"
                      onClick={() => onDelete && onDelete(notification.id)}
                    >
                      <Close size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </PanelSection>
    </div>
  );
};

// ===========================================
// Statistika (sve u jednom tabu)
// ===========================================
const StatsTab = ({ stats }) => {
  if (!stats) return <EmptyState />;
  return (
    <div className="hp-stack">
      <GroupTitle title="Korisnici" icon={UserMultiple}>
        <ResidentsTab data={stats.residents} />
      </GroupTitle>

      <GroupTitle title="Sobe" icon={Building}>
        <RoomsTab data={stats.rooms} />
      </GroupTitle>

      <GroupTitle title="Zaposleni" icon={User}>
        <EmployeesTab data={stats.employees} />
      </GroupTitle>

      <GroupTitle title="Zadaci i aktivnosti" icon={Task}>
        <TasksTab
          tasks={stats.tasks}
          activities={stats.activities}
          shifts={stats.shifts}
        />
      </GroupTitle>

      <GroupTitle title="Finansije" icon={Money}>
        <FinanceTab data={stats.budget} />
      </GroupTitle>

      <GroupTitle title="Akcije" icon={Warning}>
        <ActionsTab data={stats.actionsNeeded} />
      </GroupTitle>
    </div>
  );
};

// ===========================================
// Glavna komponenta
// ===========================================

// `NotificationProvider` iz App.js obavija sve rute, ali se „Panel doma" u
// testovima renderuje i bez njega. Tada context nije dostupan (null), pa tab
// „Obaveštenja" prikazuje prazno stanje umesto da baci grešku.
const useOptionalNotifications = () => useContext(NotificationContext);

const HomePanel = () => {
  const { theme } = useTheme();
  const isDark = theme === 'g100';
  const notificationsContext = useOptionalNotifications();
  const unreadNotifications = notificationsContext?.unreadCount || 0;

  const [searchParams] = useSearchParams();

  // URL parametar `?tab=` ima prednost (npr. prečica sa admin dashboarda
  // „Statistika doma" vodi na /panel-doma?tab=stats)
  const getInitialTab = () => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && TABS.some((t) => t.id === tabFromUrl)) return tabFromUrl;
    return 'info';
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [institution, setInstitution] = useState(null);
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const tabListRef = useRef(null);
  const prevTabIndexRef = useRef(TABS.findIndex((t) => t.id === getInitialTab()));

  // Kao na mobilnom profilu rezidenta: tab traka se horizontalno skroluje, a
  // aktivni tab se sam dovodi u vidno polje. Kada se ide udesno, aktivni tab se
  // poravnava sa LEVOM ivicom trake; kada se ide ulevo, sa DESNOM (suprotan
  // smer) - identično kao `.residents1-tabs__list` u ResidentProfile.js.
  const scrollActiveTabIntoView = useCallback(() => {
    const listEl = tabListRef.current;
    if (!listEl) return;
    const activeTabEl = listEl.querySelector('.hp-tab--active');
    if (!activeTabEl) return;

    const currentIndex = TABS.findIndex((t) => t.id === activeTab);
    const prevIndex = prevTabIndexRef.current;
    const goingRight = currentIndex >= prevIndex;

    // getBoundingClientRect je precizan i nezavisan od roditelja/skrola.
    const listRect = listEl.getBoundingClientRect();
    const tabRect = activeTabEl.getBoundingClientRect();

    const targetScrollLeft = goingRight
      ? listEl.scrollLeft + (tabRect.left - listRect.left)
      : listEl.scrollLeft + (tabRect.right - listRect.right);

    if (typeof listEl.scrollTo === 'function') {
      listEl.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
    } else {
      listEl.scrollLeft = targetScrollLeft;
    }
    prevTabIndexRef.current = currentIndex;
  }, [activeTab]);

  // Skrolujemo u sledećem frejmu: tada je layout (i `--active` klasa) sigurno
  // primenjen, pa strelica "stigne" aktivni tab i na sporijim telefonima.
  useEffect(() => {
    const frame = window.requestAnimationFrame(scrollActiveTabIntoView);
    return () => window.cancelAnimationFrame(frame);
  }, [scrollActiveTabIntoView]);

  // Pri promeni širine ekrana / orijentacije vrati aktivni tab u vidno polje.
  useEffect(() => {
    const handleResize = () => scrollActiveTabIntoView();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [scrollActiveTabIntoView]);

  // Ako se `?tab=` promeni dok je panel otvoren, prebaci na taj tab
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && TABS.some((t) => t.id === tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  // Tab „Obaveštenja": pri otvaranju osvežimo listu preko REST-a (da prikažemo
  // i ono što je eventualno promaklo dok SignalR konekcija nije bila aktivna).
  const refetchNotifications = notificationsContext?.refetch;
  useEffect(() => {
    if (activeTab === 'notifications' && refetchNotifications) {
      refetchNotifications();
    }
  }, [activeTab, refetchNotifications]);

  // Keyboard navigation for tabs (ArrowLeft/ArrowRight/Home/End).
  const handleTabKeyDown = (e, index) => {
    let newIndex = index;
    if (e.key === 'ArrowRight') {
      newIndex = (index + 1) % TABS.length;
    } else if (e.key === 'ArrowLeft') {
      newIndex = (index - 1 + TABS.length) % TABS.length;
    } else if (e.key === 'Home') {
      newIndex = 0;
    } else if (e.key === 'End') {
      newIndex = TABS.length - 1;
    } else {
      return;
    }

    e.preventDefault();
    setActiveTab(TABS[newIndex].id);
    const tabButtons = tabListRef.current?.querySelectorAll('.hp-tab');
    tabButtons?.[newIndex]?.focus();
  };

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [instRes, settingsRes, statsRes] = await Promise.all([
          axiosInstance.get('/api/Institution'),
          axiosInstance.get('/api/InstitutionSettings'),
          axiosInstance.get('/api/Statistics/dashboard'),
        ]);
        if (!mounted) return;
        setInstitution(instRes?.data?.data ?? null);
        setSettings(settingsRes?.data?.data ?? null);
        setStats(statsRes?.data?.data ?? null);
      } catch (err) {
        if (!mounted) return;
        console.error('Greška pri učitavanju podataka o domu:', err);
        setError('Greška pri učitavanju podataka o domu.');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div className={`hp-page ${isDark ? 'hp-page--dark' : ''}`}>
      <div className="hp-container">
        <header className="hp-header">
          <div className="hp-header__icon">
            <Building size={28} />
          </div>
          <div>
            <h1 className="hp-header__title">
              {institution?.name || 'Panel doma'}
            </h1>
          </div>
        </header>

        <nav className="hp-tabs" aria-label="Navigacija panela doma">
          <div
            className="hp-tabs__list"
            role="tablist"
            ref={tabListRef}
            aria-label="Sekcije panela doma"
          >
            {TABS.map((tab, index) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  role="tab"
                  id={`hp-tab-${tab.id}`}
                  aria-selected={activeTab === tab.id}
                  aria-controls={`hp-panel-${tab.id}`}
                  tabIndex={activeTab === tab.id ? 0 : -1}
                  className={`hp-tab ${activeTab === tab.id ? 'hp-tab--active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                  onKeyDown={(e) => handleTabKeyDown(e, index)}
                >
                  <Icon size={16} />
                  <span>{tab.label}</span>
                  {tab.id === 'notifications' && unreadNotifications > 0 && (
                    <span className="hp-tab__badge">{unreadNotifications}</span>
                  )}
                </button>
              );
            })}
          </div>
        </nav>

        <div
          className="hp-panel"
          role="tabpanel"
          id={`hp-panel-${activeTab}`}
          aria-labelledby={`hp-tab-${activeTab}`}
        >
          {error && (
            <InlineNotification
              kind="error"
              title="Greška"
              subtitle={error}
              lowContrast
              hideCloseButton
              className="hp-notification"
            />
          )}

          {activeTab === 'notifications' ? (
            <NotificationsTab
              notifications={notificationsContext?.notifications || []}
              unreadCount={unreadNotifications}
              loading={notificationsContext?.loading || false}
              error={notificationsContext?.error || null}
              onMarkAsRead={notificationsContext?.markAsRead}
              onMarkAllAsRead={notificationsContext?.markAllAsRead}
              onDelete={notificationsContext?.deleteNotification}
            />
          ) : loading ? (
            <LoadingState />
          ) : (
            <>
              {activeTab === 'info' && (
                <InfoTab
                  institution={institution}
                  settings={settings}
                  onSettingsChange={setSettings}
                />
              )}
              {activeTab === 'stats' && <StatsTab stats={stats} />}
              {activeTab === 'actions' && <ActionNeeded />}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default HomePanel;
